
import { supabase } from '@/integrations/supabase/client';
import { 
  CertificateTemplate, 
  CertificateTemplateElement,
  CertificateTemplateType 
} from '@/types/certificateTemplate';
import { v4 as uuidv4 } from 'uuid';

class CertificateTemplateSupabaseService {
  private syncStatus: 'synced' | 'pending' | 'offline' = 'offline';
  private lastSyncAttempt: Date | null = null;
  private syncQueue: Set<string> = new Set();

  async syncAllTemplates(): Promise<CertificateTemplate[]> {
    try {
      console.log('Starting certificate templates sync from Supabase...');
      this.syncStatus = 'pending';

      // Fetch all certificate templates
      const { data: templatesData, error: templatesError } = await supabase
        .from('certificate_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (templatesError) {
        console.error('Error syncing certificate templates:', templatesError);
        this.syncStatus = 'offline';
        return [];
      }

      if (!templatesData || templatesData.length === 0) {
        console.log('No certificate templates found in Supabase');
        this.syncStatus = 'synced';
        return [];
      }

      // Fetch elements for all templates
      const { data: elementsData, error: elementsError } = await supabase
        .from('certificate_template_elements')
        .select('*')
        .order('created_at', { ascending: true });

      if (elementsError) {
        console.error('Error syncing certificate template elements:', elementsError);
        this.syncStatus = 'offline';
        return [];
      }

      // Group elements by template
      const elementsByTemplate = new Map<string, any[]>();
      if (elementsData) {
        elementsData.forEach(element => {
          if (!elementsByTemplate.has(element.template_id)) {
            elementsByTemplate.set(element.template_id, []);
          }
          elementsByTemplate.get(element.template_id)!.push(element);
        });
      }

      // Transform to local structure
      const enrichedTemplates: CertificateTemplate[] = templatesData.map(template => ({
        id: template.id,
        name: template.name,
        type: template.type as CertificateTemplateType,
        description: template.description || '',
        thumbnail: template.thumbnail,
        backgroundColor: template.background_color,
        backgroundImage: template.background_image,
        size: {
          width: template.size_width,
          height: template.size_height
        },
        elements: (elementsByTemplate.get(template.id) || []).map(element => ({
          id: element.id,
          type: element.element_type,
          content: element.content,
          position: {
            x: element.position_x,
            y: element.position_y
          },
          style: element.style || {},
          dynamic: element.is_dynamic || false,
          dataField: element.data_field,
          name: element.element_name
        })),
        isDefault: template.is_default || false,
        metadata: (template.metadata && typeof template.metadata === 'object') ? template.metadata as Record<string, any> : {},
        createdAt: template.created_at,
        updatedAt: template.updated_at,
        createdBy: template.created_by
      }));

      this.syncStatus = 'synced';
      this.lastSyncAttempt = new Date();
      console.log(`Successfully synced ${enrichedTemplates.length} certificate templates from Supabase`);
      
      return enrichedTemplates;
    } catch (error) {
      console.error('Failed to sync certificate templates from Supabase:', error);
      this.syncStatus = 'offline';
      return [];
    }
  }

  async saveCertificateTemplate(template: CertificateTemplate): Promise<void> {
    try {
      console.log(`Syncing certificate template ${template.id} to Supabase...`);
      this.syncQueue.add(template.id);

      // Check if template exists
      const { data: existingTemplate } = await supabase
        .from('certificate_templates')
        .select('id')
        .eq('id', template.id)
        .single();

      // Helper function to ensure date is a string
      const ensureDateString = (date: string | Date): string => {
        if (date instanceof Date) {
          return date.toISOString();
        }
        return date;
      };

      const templateData = {
        name: template.name,
        type: template.type,
        description: template.description,
        thumbnail: template.thumbnail,
        background_color: template.backgroundColor,
        background_image: template.backgroundImage,
        size_width: template.size.width,
        size_height: template.size.height,
        is_default: template.isDefault,
        metadata: template.metadata,
        created_at: ensureDateString(template.createdAt),
        updated_at: ensureDateString(template.updatedAt),
        created_by: template.createdBy
      };

      let templateError;
      if (existingTemplate) {
        // Update existing template
        const { error } = await supabase
          .from('certificate_templates')
          .update(templateData)
          .eq('id', template.id);
        templateError = error;
      } else {
        // Insert new template
        const { error } = await supabase
          .from('certificate_templates')
          .insert({
            id: template.id,
            ...templateData
          });
        templateError = error;
      }

      if (templateError) {
        console.error('Error saving certificate template to Supabase:', templateError);
        this.syncQueue.delete(template.id);
        return;
      }

      // Save elements
      if (template.elements && template.elements.length > 0) {
        await this.syncTemplateElements(template.id, template.elements);
      }

      this.syncQueue.delete(template.id);
      console.log(`Successfully synced certificate template ${template.id} to Supabase`);
    } catch (error) {
      console.error('Failed to save certificate template to Supabase:', error);
      this.syncQueue.delete(template.id);
    }
  }

  private async syncTemplateElements(templateId: string, elements: CertificateTemplateElement[]): Promise<void> {
    try {
      // Delete existing elements for this template
      await supabase
        .from('certificate_template_elements')
        .delete()
        .eq('template_id', templateId);

      // Insert new elements
      if (elements.length > 0) {
        const { error } = await supabase
          .from('certificate_template_elements')
          .insert(elements.map(element => ({
            id: element.id,
            template_id: templateId,
            element_type: element.type,
            content: element.content,
            position_x: element.position.x,
            position_y: element.position.y,
            style: element.style || {},
            is_dynamic: element.dynamic || false,
            data_field: element.dataField,
            element_name: element.name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })));

        if (error) {
          console.error('Error syncing certificate template elements:', error);
        }
      }
    } catch (error) {
      console.error('Failed to sync certificate template elements:', error);
    }
  }

  async deleteCertificateTemplate(templateId: string): Promise<void> {
    try {
      console.log(`Deleting certificate template ${templateId} from Supabase...`);
      
      const { error } = await supabase
        .from('certificate_templates')
        .delete()
        .eq('id', templateId);

      if (error) {
        console.error('Error deleting certificate template from Supabase:', error);
        return;
      }

      console.log(`Successfully deleted certificate template ${templateId} from Supabase`);
    } catch (error) {
      console.error('Failed to delete certificate template from Supabase:', error);
    }
  }

  async saveCertificateInstance(instance: {
    id: string;
    templateId: string;
    studentId?: number;
    certificateNumber: string;
    programTitle: string;
    programCategory: string;
    certificationLevel: string;
    issuedBy: string;
    verificationCode: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('certificate_instances')
        .upsert({
          id: instance.id,
          template_id: instance.templateId,
          student_id: instance.studentId,
          certificate_number: instance.certificateNumber,
          program_title: instance.programTitle,
          program_category: instance.programCategory,
          certification_level: instance.certificationLevel,
          issued_by: instance.issuedBy,
          verification_code: instance.verificationCode,
          metadata: instance.metadata || {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving certificate instance:', error);
      } else {
        console.log(`Successfully saved certificate instance ${instance.id}`);
      }
    } catch (error) {
      console.error('Failed to save certificate instance:', error);
    }
  }

  async saveCertificateBackup(backup: {
    id: string;
    description: string;
    backupData: any;
    itemCount: number;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('certificate_backups')
        .upsert({
          id: backup.id,
          description: backup.description,
          backup_data: backup.backupData,
          item_count: backup.itemCount,
          created_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving certificate backup:', error);
      } else {
        console.log(`Successfully saved certificate backup ${backup.id}`);
      }
    } catch (error) {
      console.error('Failed to save certificate backup:', error);
    }
  }

  getSyncStatus(): 'synced' | 'pending' | 'offline' {
    return this.syncStatus;
  }

  getLastSyncAttempt(): Date | null {
    return this.lastSyncAttempt;
  }

  getPendingSyncIds(): string[] {
    return Array.from(this.syncQueue);
  }

  async testConnection(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('certificate_templates')
        .select('count')
        .limit(1);
      
      return !error;
    } catch (error) {
      return false;
    }
  }
}

export const certificateTemplateSupabaseService = new CertificateTemplateSupabaseService();
